// @ts-nocheck
/* eslint-disable react-hooks/exhaustive-deps */
/**
 * usePDFViewer - PDF 阅读器核心 Hook
 *
 * 注意：此 Hook 包含大量状态，计划逐步拆分为独立的小 Hooks：
 * - useNotification (已提取)
 * - useImageAnalysis (已提取)
 * - useQASessions (待提取)
 * - useAnnotations (待提取)
 *
 * 相关文档: docs/架构设计.md
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import {
  generatePDFFingerprint,
  loadAnnotationsByFingerprint,
  saveAnnotationsToStorage,
  deleteAnnotationFromStorage,
  deleteAnnotationsBatch
} from '../components/pdfUtils';
import { apiPost, apiDelete } from '../utils/apiClient';
import { logger } from '../utils/logger';
import type {
  QASession,
  QASessionMode,
  Annotation,
  AnnotationsMap,
  ReadingSession,
  PDFInfo,
  StickyNoteParams,
  UsePDFViewerReturn,
} from '../types/pdf-viewer';

export function usePDFViewer(
  originalFile: File | null,
  currentPage: number,
  sessionMode = false,
  currentSession: ReadingSession | null = null,
  existingFingerprint: string | null = null,
): UsePDFViewerReturn {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [scale, setScale] = useState(1.0);
  const [annotations, setAnnotations] = useState<AnnotationsMap>({});
  const [pdfFingerprint, setPdfFingerprint] = useState('');
  const [pdfInfo, setPdfInfo] = useState<PDFInfo | null>(null);
  const [isGeneratingFingerprint, setIsGeneratingFingerprint] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [qaSessions, setQaSessions] = useState<QASession[]>([]);
  const [showImagePanel, setShowImagePanel] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionRect, setSelectionRect] = useState<any>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [imageQuestion, setImageQuestion] = useState('');
  const [imageAnswer, setImageAnswer] = useState('');
  const [imageQuestioning, setImageQuestioning] = useState(false);
  const [showAnnotationsList, setShowAnnotationsList] = useState(false);
  const [showAnnotationPanel, setShowAnnotationPanel] = useState(false);
  const [currentAnnotation, setCurrentAnnotation] = useState<Annotation | null>(null);
  const [tagFilter, setTagFilter] = useState('all');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [annotationToDelete, setAnnotationToDelete] = useState<string | null>(null);
  const [showSummary, setShowSummary] = useState(false);
  const [summary, setSummary] = useState('');
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [isGeneratingSpeech, setIsGeneratingSpeech] = useState(false);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [audioCache] = useState(new Map<string, string>());
  // 苏格拉底对话状态
  const [showSocraticDialog, setShowSocraticDialog] = useState(false);
  const [socraticContext, setSocraticContext] = useState('');
  const [socraticHistory, setSocraticHistory] = useState<Array<{ role: string; content: string }>>([]);

  const pageRef = useRef<HTMLDivElement>(null!);
  const originalFileRef = useRef(originalFile);
  const currentPageRef = useRef(currentPage);
  const lastSessionCreationRef = useRef<{ text: string; timestamp: number; sessionId?: string }>({ text: '', timestamp: 0 });
  const SESSION_DEBOUNCE_TIME = 500;
  
  const generateSessionName = useCallback((text: string, page: number) => {
    const cleanText = text.replace(/\s+/g, ' ').trim();
    const preview = cleanText.length > 20 ? cleanText.substring(0, 20) + '...' : cleanText;
    return `第${page}页：${preview}`;
  }, []);
  
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  const cleanupAudio = useCallback((audio: HTMLAudioElement | null = currentAudio) => {
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      const audioUrl = audio.src;
      for (const [key, cachedUrl] of audioCache.entries()) {
        if (cachedUrl === audioUrl) {
          URL.revokeObjectURL(cachedUrl);
          audioCache.delete(key);
          break;
        }
      }
      if (audio === currentAudio) {
        setCurrentAudio(null);
      }
    }
  }, [currentAudio, audioCache]);

  const showNotificationMessage = useCallback((message: string) => {
    logger.log('通知消息:', message);
    setNotificationMessage(message);
    setShowNotification(true);
    setTimeout(() => {
      setShowNotification(false);
    }, 3000);
  }, []);

  const viewAnnotationCallback = useCallback((annotation: any) => {
    cleanupAudio();
    setIsGeneratingSpeech(false);

    // 特殊处理：如果是快速浏览标注，允许恢复会话继续深入理解
    if (annotation.subType === 'guide.quick_scan' && !annotation.answer.includes('【第二轮深入理解】')) {
      // 检查是否已有相同的活动会话
      const existingSession = qaSessions.find(s => s.savedAnnotationId === annotation.id);

      if (!existingSession) {
        // 恢复会话
        const sessionId = `qa_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const newSession: QASession = {
          id: sessionId,
          sessionName: annotation.sessionName || annotation.question,
          selectedText: annotation.text,
          editableText: annotation.text,
          position: annotation.position,
          page: annotation.page,
          question: '',
          answer: '',
          status: 'answered',
          selectedTag: annotation.tag || 'note',
          timestamp: annotation.timestamp,
          conversationHistory: [],
          mode: 'quick_scan',
          aiQuickAnalysis: annotation.answer,
          aiDeepAnalysis: null,
          isAnalyzing: false,
          quickScanSaved: true,
          savedAnnotationId: annotation.id
        };

        setQaSessions(prev => [...prev, newSession]);
        showNotificationMessage('已恢复快速浏览会话，可继续进行深入理解');
        return;
      }
    }

    setCurrentAnnotation(annotation);
    setShowAnnotationPanel(true);
    setShowImagePanel(false);
  }, [cleanupAudio, qaSessions, showNotificationMessage]);

  const initializePDF = useCallback(async () => {
    if (originalFileRef.current) {
      logger.log('🎬 开始初始化PDF...');
      setIsGeneratingFingerprint(true);

      try {
        // 🔧 优先使用已有的指纹（从 PDF 库选择时传入），否则才生成新指纹
        let fingerprint;
        if (existingFingerprint) {
          fingerprint = existingFingerprint;
          logger.log('📌 使用已有指纹（来自PDF库）:', fingerprint);
        } else {
          fingerprint = await generatePDFFingerprint(originalFileRef.current);
          logger.log('📌 PDF指纹生成成功（新上传）:', fingerprint);
        }
        setPdfFingerprint(fingerprint);

        // 根据会话模式加载标注
        let count = 0;
        if (sessionMode && !currentSession) {
          // 会话模式下但未选择会话，不加载任何标注
          logger.log('⚠️ 会话模式下未选择会话，不加载标注');
          setAnnotations({});
        } else {
          const sessionIdForLoading = sessionMode && currentSession ? currentSession.id : null;
          const result = await loadAnnotationsByFingerprint(fingerprint, sessionIdForLoading) as any;
          const annotations = result.annotations;
          count = result.count;
          const modeDesc = sessionMode && currentSession ? `会话「${currentSession.name || currentSession.session_name || '未命名'}」` : '独立模式';
          logger.log(`✅ 加载历史标注(${modeDesc}):`, count, '个');
          setAnnotations(annotations);

          if (count > 0) {
            showNotificationMessage(`检测到相同PDF文件，已加载 ${count} 个历史标注`);
          }
        }

        logger.log('✅ 标注加载完成，返回fingerprint以便恢复阅读位置');
        // 标注加载完成后，返回fingerprint以便后续恢复阅读位置
        return fingerprint;
      } catch (error: any) {
        console.error('❌ 初始化PDF失败:', error);
        showNotificationMessage('PDF初始化失败: ' + error.message);
        return null;
      } finally {
        setIsGeneratingFingerprint(false);
      }
    }
    return null;
  }, [sessionMode, currentSession, showNotificationMessage, existingFingerprint]);

  // 监听会话切换，重新加载标注
  useEffect(() => {
    // 如果PDF指纹已生成，且会话模式或当前会话发生变化，重新加载标注
    if (pdfFingerprint) {
      const reloadAnnotations = async () => {
        const sessionIdForLoading = sessionMode && currentSession ? currentSession.id : null;
        const { annotations: freshAnnotations, count } = await loadAnnotationsByFingerprint(pdfFingerprint, sessionIdForLoading) as any;
        const modeDesc = sessionMode && currentSession ? `会话「${currentSession.name || currentSession.session_name || '未命名'}」` : '独立模式';
        logger.log(`会话切换，重新加载标注(${modeDesc}):`, count, '个');
        setAnnotations(freshAnnotations);
      };
      reloadAnnotations();
    }
  }, [sessionMode, currentSession, pdfFingerprint]);

  const createQASession = useCallback((selectedText: string, position: { x: number; y: number } | null, mode: QASessionMode = 'normal') => {
    const now = Date.now();
    const lastCreation = lastSessionCreationRef.current;
    
    if (
      lastCreation.text === selectedText && 
      now - lastCreation.timestamp < SESSION_DEBOUNCE_TIME
    ) {
      logger.log('防抖：忽略重复的会话创建请求');
      return lastCreation.sessionId;
    }
    
    const actualCurrentPage = currentPageRef.current;
    const sessionId = `qa_${now}_${Math.random().toString(36).substr(2, 9)}`;
    const sessionName = generateSessionName(selectedText, actualCurrentPage);
    
    const newSession: QASession = {
      id: sessionId,
      sessionName: sessionName,
      selectedText: selectedText,
      editableText: selectedText,
      position: position,
      page: actualCurrentPage,
      question: '',
      answer: '',
      status: 'ready',
      selectedTag: 'note',
      timestamp: new Date().toISOString(),
      conversationHistory: [],
      mode: mode,
      aiQuickAnalysis: null,
      aiDeepAnalysis: null,
      isAnalyzing: false,
      quickScanSaved: false,
      savedAnnotationId: null
    };

    logger.log('创建新问答会话:', sessionId, '页面:', actualCurrentPage, '名字:', sessionName, '模式:', mode);
    
    lastSessionCreationRef.current = {
      text: selectedText,
      timestamp: now,
      sessionId: sessionId
    };
    
    setQaSessions(prev => {
      if (prev.find(s => s.id === sessionId)) {
        console.warn('警告：尝试添加重复的会话ID:', sessionId);
        return prev;
      }
      return [...prev, newSession];
    });
    
    return sessionId;
  }, [generateSessionName]);

  const handleQuickScanAnalysis = useCallback(async (sessionId: string) => {
    const session = qaSessions.find(s => s.id === sessionId);
    if (!session) {
      showNotificationMessage('会话不存在');
      return;
    }
    
    logger.log('开始快速浏览分析:', sessionId, '会话模式:', sessionMode);
    updateQASession(sessionId, { status: 'questioning' as const, isAnalyzing: true });

    try {
      // 构建请求参数，支持会话感知
      const requestBody = {
        selectedText: session.editableText,
        session_id: sessionMode && currentSession ? currentSession.id : null,
        current_page: currentPageRef.current,
        pdf_fingerprint: pdfFingerprint
      };

      logger.log('[Quick Scan] 请求参数:', requestBody);

      const result = await apiPost('/api/guide/quick-scan', requestBody) as any;
      
      if (result.success) {
        updateQASession(sessionId, {
          status: 'answered' as const,
          isAnalyzing: false,
          aiQuickAnalysis: result.analysis
        });
        showNotificationMessage('快速浏览分析完成');
      } else {
        throw new Error(result.error || '分析失败');
      }
    } catch (error: any) {
      console.error('快速浏览分析失败:', error);
      updateQASession(sessionId, {
        status: 'error' as const,
        isAnalyzing: false,
        aiQuickAnalysis: `分析失败: ${error.message}`
      });
      showNotificationMessage('快速浏览分析失败');
    }
  }, [qaSessions, showNotificationMessage]);

  const handleDeepDiveAnalysis = useCallback(async (sessionId: string) => {
    const session = qaSessions.find(s => s.id === sessionId);
    if (!session) {
      showNotificationMessage('会话不存在');
      return;
    }
    
    logger.log('开始深入理解分析:', sessionId, '会话模式:', sessionMode);
    updateQASession(sessionId, { status: 'questioning' as const, isAnalyzing: true });

    try {
      // 构建请求参数，支持会话感知
      const requestBody = {
        selectedText: session.editableText,
        quickScanContent: session.aiQuickAnalysis || '',
        session_id: sessionMode && currentSession ? currentSession.id : null,
        current_page: currentPageRef.current,
        pdf_fingerprint: pdfFingerprint
      };

      logger.log('[Deep Dive] 请求参数:', requestBody);

      const result = await apiPost('/api/guide/deep-dive', requestBody) as any;
      
      if (result.success) {
        updateQASession(sessionId, {
          status: 'answered' as const,
          isAnalyzing: false,
          aiDeepAnalysis: result.analysis
        });
        showNotificationMessage('深入理解分析完成');
      } else {
        throw new Error(result.error || '分析失败');
      }
    } catch (error: any) {
      console.error('深入理解分析失败:', error);
      updateQASession(sessionId, {
        status: 'error' as const,
        isAnalyzing: false,
        aiDeepAnalysis: `分析失败: ${error.message}`
      });
      showNotificationMessage('深入理解分析失败');
    }
  }, [qaSessions, showNotificationMessage]);

  const convertToDeepDive = useCallback(async (sessionId: string) => {
    const session = qaSessions.find(s => s.id === sessionId);
    if (!session || !session.aiQuickAnalysis) {
      showNotificationMessage('请先完成快速浏览');
      return;
    }
    
    logger.log('转为深入理解模式:', sessionId, '会话模式:', sessionMode);
    updateQASession(sessionId, {
      mode: 'deep_dive',
      status: 'questioning' as const,
      isAnalyzing: true
    });

    try {
      // 构建请求参数，支持会话感知
      const requestBody = {
        selectedText: session.editableText,
        quickScanContent: session.aiQuickAnalysis,
        session_id: sessionMode && currentSession ? currentSession.id : null,
        current_page: currentPageRef.current,
        pdf_fingerprint: pdfFingerprint
      };

      logger.log('[Convert to Deep Dive] 请求参数:', requestBody);

      const result = await apiPost('/api/guide/deep-dive', requestBody) as any;
      
      if (result.success) {
        updateQASession(sessionId, {
          status: 'answered' as const,
          isAnalyzing: false,
          aiDeepAnalysis: result.analysis
        });
        showNotificationMessage('深入理解分析完成');
      } else {
        throw new Error(result.error || '分析失败');
      }
    } catch (error: any) {
      console.error('深入理解分析失败:', error);
      updateQASession(sessionId, {
        status: 'error' as const,
        isAnalyzing: false,
        aiDeepAnalysis: `分析失败: ${error.message}`
      });
      showNotificationMessage('深入理解分析失败');
    }
  }, [qaSessions, showNotificationMessage]);

  const generateCriticalSummary = useCallback(async () => {
    if (!pdfFingerprint) {
      showNotificationMessage('PDF未加载');
      return;
    }
    
    const deepDiveCount = qaSessions.filter(s => s.mode === 'deep_dive' && s.aiDeepAnalysis).length;
    if (deepDiveCount < 2) {
      showNotificationMessage('请至少完成2个深入理解后再生成总结');
      return;
    }
    
    setGeneratingSummary(true);
    
    try {
      const result = await apiPost('/api/guide/critical-summary', { pdfFingerprint }) as any;
      
      if (result.success) {
        setSummary(result.summary);
        setShowSummary(true);
        showNotificationMessage('批判性总结生成完成');
      } else {
        throw new Error(result.error || '生成失败');
      }
    } catch (error: any) {
      console.error('批判性总结失败:', error);
      showNotificationMessage('批判性总结失败: ' + error.message);
    } finally {
      setGeneratingSummary(false);
    }
  }, [pdfFingerprint, qaSessions, showNotificationMessage]);

  const updateQASession = useCallback((sessionId: string, updates: Partial<QASession>) => {
    logger.log('更新会话:', sessionId, updates);
    setQaSessions(prev => prev.map(session => 
      session.id === sessionId ? { ...session, ...updates } : session
    ));
  }, []);

  const removeQASession = useCallback((sessionId: string) => {
    logger.log('删除问答会话:', sessionId);
    setQaSessions(prev => prev.filter(session => session.id !== sessionId));
  }, []);

  const handleSessionQuestion = useCallback(async (sessionId: string) => {
    const session = qaSessions.find(s => s.id === sessionId);
    if (!session || !session.question.trim() || !session.editableText.trim()) {
      showNotificationMessage('请输入问题和确认文本内容');
      return;
    }

    logger.log('开始处理问答:', sessionId, session.question);
    logger.log('会话模式:', sessionMode, '当前会话:', currentSession);
    updateQASession(sessionId, { status: 'questioning' });

    try {
      // 使用新的 /api/ask-question API，支持会话模式
      const requestBody: Record<string, any> = {
        text: session.editableText,
        question: session.question,
        current_page: currentPage,
        pdf_fingerprint: pdfFingerprint
      };

      // 如果是会话模式，添加session_id
      if (sessionMode && currentSession) {
        requestBody.session_id = currentSession.id;
        logger.log('使用会话模式，session_id:', currentSession.id);
      } else {
        logger.log('使用独立模式');
      }

      const result = await apiPost('/api/ask-question', requestBody) as any;

      if (!result.answer) {
        throw new Error('API返回的答案为空');
      }

      updateQASession(sessionId, {
        status: 'answered' as const,
        answer: result.answer,
        question: ''
      });

      const modeText = sessionMode ? '会话模式' : '独立模式';
      showNotificationMessage(`AI回答完成（${modeText}）`);
    } catch (error: any) {
      console.error('问答失败:', error);
      updateQASession(sessionId, {
        status: 'error' as const,
        answer: '抱歉，问答服务出现错误：' + error.message + '。请重试。'
      });
      showNotificationMessage('问答失败: ' + error.message);
    }
  }, [qaSessions, showNotificationMessage, updateQASession, sessionMode, currentSession, currentPage, pdfFingerprint]);

  // ✅ 优化后的保存标注逻辑
  const saveSessionAsAnnotation = useCallback(async (sessionId: string) => {
    logger.log('开始保存标注，会话ID:', sessionId);
    
    const session = qaSessions.find(s => s.id === sessionId);
    if (!session) {
      console.error('会话不存在:', sessionId);
      showNotificationMessage('会话不存在');
      return;
    }
    
    const targetPage = session.page;
    const hasContent = session.aiQuickAnalysis || session.aiDeepAnalysis || (session.answer && session.answer.trim().length > 0);
    
    if (!hasContent) {
      logger.log('没有可保存的内容');
      showNotificationMessage('请先获取AI分析或回答');
      return;
    }

    if (!pdfFingerprint) {
      logger.log('PDF指纹未生成');
      showNotificationMessage('PDF指纹生成中，请稍候重试');
      return;
    }

    logger.log('开始准备标注数据，目标页面:', targetPage);
    
    try {
      // 判断保存类型
      const isQuickScanOnly = session.mode === 'quick_scan' && !session.aiDeepAnalysis;
      const isDeepDiveUpdate = (session.mode === 'deep_dive' || session.aiDeepAnalysis) && session.quickScanSaved && session.savedAnnotationId;
      
      let annotationId: string;
      let shouldRemoveSession = true;
      
      // 如果是更新已保存的快速浏览标注
      if (isDeepDiveUpdate) {
        annotationId = session.savedAnnotationId!;
        logger.log('更新已保存的快速浏览标注:', annotationId);
      } else {
        annotationId = `${targetPage}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        logger.log('创建新标注:', annotationId);
      }
      
      let relativePosition = null;
      if (session.position) {
        relativePosition = {
          x: session.position.x / scale,
          y: session.position.y / scale
        };
      }

      let finalQuestion, finalAnswer, sessionType, subType;

      if (session.mode === 'quick_scan' && session.aiQuickAnalysis && !session.aiDeepAnalysis) {
        // 仅快速浏览
        finalQuestion = `快速浏览：${session.sessionName || '未命名'}`;
        finalAnswer = session.aiQuickAnalysis;
        sessionType = 'guide';
        subType = 'guide.quick_scan';
        shouldRemoveSession = false; // 不删除会话，允许继续深入理解
      } else if (session.aiDeepAnalysis) {
        // 包含深入理解（可能有快速浏览）
        finalQuestion = `深入理解：${session.sessionName || '未命名'}`;
        if (session.aiQuickAnalysis) {
          finalAnswer = `【第一轮快速浏览】\n${session.aiQuickAnalysis}\n\n【第二轮深入理解】\n${session.aiDeepAnalysis}`;
        } else {
          finalAnswer = session.aiDeepAnalysis;
        }
        sessionType = 'guide';
        subType = 'guide.deep_dive';
        shouldRemoveSession = true; // 深入理解完成后删除会话
      } else if (session.conversationHistory && session.conversationHistory.length >= 2) {
        const conversations = [];
        const history = session.conversationHistory;

        for (let i = 1; i < history.length; i += 2) {
          if (history[i] && history[i + 1]) {
            const userMsg = history[i].content.replace(/基于以下文档内容回答问题：.*?请根据文档内容准确回答问题[^。]*。回答要简洁准确。/s, '').trim();
            const assistantMsg = history[i + 1].content;
            conversations.push(`问: ${userMsg}\n答: ${assistantMsg}`);
          }
        }

        finalQuestion = session.sessionName || '多轮对话记录';
        finalAnswer = conversations.join('\n\n---\n\n');
        sessionType = 'qa';
        subType = 'qa.conversation';
      } else if (session.mode === 'manual') {
        // 手动标注
        finalQuestion = session.sessionName || '手动标注';
        finalAnswer = session.answer || session.editableText;
        sessionType = 'manual';
        const tag = session.selectedTag || 'note';
        subType = `manual.${tag}`;
      } else {
        // 普通问答
        finalQuestion = session.sessionName || (session.question && session.question.trim() ? session.question : '文档问答');
        finalAnswer = session.answer;
        sessionType = 'qa';
        subType = 'qa.text';
      }

      const newAnnotation = {
        id: annotationId,
        page: targetPage,
        text: session.editableText,
        question: finalQuestion,
        answer: finalAnswer,
        tag: session.selectedTag || 'note',
        timestamp: isDeepDiveUpdate ? annotations[annotationId]?.timestamp : new Date().toISOString(),
        pdfFingerprint: pdfFingerprint,
        position: relativePosition,
        type: 'text',
        sessionType: sessionType,
        subType: subType,
        sessionName: session.sessionName,
        readingSessionId: sessionMode && currentSession ? currentSession.id : null  // 添加会话ID
      };

      logger.log('准备保存标注 - sessionType:', sessionType, 'subType:', subType, 'mode:', session.mode);

      const newAnnotations = {
        ...annotations,
        [annotationId]: newAnnotation
      };

      logger.log('调用存储API，保存到第', targetPage, '页...');
      const saved = await saveAnnotationsToStorage(pdfFingerprint, newAnnotations) as any;

      if (saved) {
        logger.log('✅ 标注保存成功，页码:', targetPage);
        logger.log('📊 检查Canvas同步条件 - sessionMode:', sessionMode, 'currentSession:', currentSession?.id, 'subType:', subType);
        setAnnotations(newAnnotations);

        // 🔧 修复：移除重复的 block 创建逻辑
        // saveAnnotationsToStorage 已经通过 annotationToBlockFormat 创建了 block
        // 这里不需要再创建，否则会导致重复（每次保存都会多创建一个）
        logger.log('📝 标注已通过 saveAnnotationsToStorage 保存为 block，跳过重复创建');

        let messageText;
        if (isQuickScanOnly) {
          messageText = `快速浏览已保存到第${targetPage}页（可继续深入理解）`;
          shouldRemoveSession = false;
        } else if (isDeepDiveUpdate) {
          messageText = `深入理解已更新到第${targetPage}页`;
          shouldRemoveSession = true;
        } else if (session.mode === 'deep_dive') {
          messageText = `深入理解已保存到第${targetPage}页`;
          shouldRemoveSession = true;
        } else if (session.conversationHistory && session.conversationHistory.length >= 2) {
          messageText = `多轮对话已保存到第${targetPage}页`;
          shouldRemoveSession = true;
        } else {
          messageText = `问答已保存到第${targetPage}页`;
          shouldRemoveSession = true;
        }

        // 根据标志统一处理会话
        if (shouldRemoveSession) {
          removeQASession(sessionId);
        } else {
          updateQASession(sessionId, {
            quickScanSaved: true,
            savedAnnotationId: annotationId
          });
        }

        showNotificationMessage(messageText);
        logger.log('保存流程完成');
      } else {
        console.error('存储API返回失败');
        showNotificationMessage('保存失败，请重试');
      }
    } catch (error: any) {
      console.error('保存标注时出错:', error);
      showNotificationMessage('保存失败: ' + error.message);
    }
  }, [pdfFingerprint, scale, annotations, showNotificationMessage, removeQASession, updateQASession, qaSessions, setAnnotations, sessionMode, currentSession]);

  // ... 其他方法保持不变 ...
  const handleImageQuestion = useCallback(async () => {
    if (!capturedImage || !imageQuestion.trim()) {
      showNotificationMessage('请确保已截图并输入问题');
      return;
    }
    
    setImageQuestioning(true);

    try {
      const result = await apiPost('/api/ask-image-question', {
        image: capturedImage,
        question: imageQuestion
      }) as any;
      setImageAnswer(result.answer);
      showNotificationMessage('图片分析完成');
    } catch (error: any) {
      console.error('图片问答失败:', error);
      setImageAnswer('抱歉，图片问答服务出现错误：' + error.message);
      showNotificationMessage('图片分析失败: ' + error.message);
    } finally {
      setImageQuestioning(false);
    }
  }, [capturedImage, imageQuestion, showNotificationMessage]);

  const generateSpeechStream = useCallback(async (text: string) => {
    if (!text || text.trim().length === 0) {
      showNotificationMessage('没有可转换的文本内容');
      return;
    }

    const textKey = text.trim();
    cleanupAudio();

    if (audioCache.has(textKey)) {
      try {
        const cachedUrl = audioCache.get(textKey)!;
        const audio = new Audio(cachedUrl);
        
        audio.oncanplay = () => {
          showNotificationMessage('开始播放语音（缓存）');
        };
        
        audio.onplay = () => {
          setCurrentAudio(audio);
        };
        
        audio.onended = () => {
          showNotificationMessage('语音播放完成');
          setCurrentAudio(null);
        };
        
        audio.onerror = () => {
          URL.revokeObjectURL(cachedUrl);
          audioCache.delete(textKey);
          generateSpeechStream(text);
        };
        
        await audio.play();
        return;
      } catch (error: any) {
        logger.log('缓存音频播放失败，重新生成');
      }
    }

    setIsGeneratingSpeech(true);
    showNotificationMessage('开始生成语音...');

    try {
      const response = await fetch('/api/generate-speech-stream', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text: textKey }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      
      if (!arrayBuffer || arrayBuffer.byteLength === 0) {
        throw new Error('音频数据为空');
      }
      
      const audioBlob = new Blob([arrayBuffer], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      
      if (audioCache.size >= 20) {
        const firstKey = audioCache.keys().next().value!;
        const oldUrl = audioCache.get(firstKey)!;
        URL.revokeObjectURL(oldUrl);
        audioCache.delete(firstKey);
      }
      audioCache.set(textKey, audioUrl);
      
      const audio = new Audio(audioUrl);
      
      audio.oncanplay = () => {
        showNotificationMessage('开始播放语音');
        setIsGeneratingSpeech(false);
      };
      
      audio.onplay = () => {
        setCurrentAudio(audio);
      };
      
      audio.onerror = (e) => {
        console.error('音频播放失败:', e);
        showNotificationMessage('音频播放失败');
        setIsGeneratingSpeech(false);
        cleanupAudio(audio);
      };
      
      audio.onended = () => {
        showNotificationMessage('语音播放完成');
        setCurrentAudio(null);
      };

      await audio.play();
      
    } catch (error: any) {
      console.error('流式语音生成失败:', error);
      showNotificationMessage('语音生成失败：' + error.message);
      setIsGeneratingSpeech(false);
    }
  }, [audioCache, cleanupAudio, showNotificationMessage]);

  const stopAudio = useCallback(() => {
    if (currentAudio) {
      cleanupAudio();
      showNotificationMessage('已停止播放');
    }
  }, [currentAudio, cleanupAudio, showNotificationMessage]);

  const confirmDeleteAnnotation = useCallback(async () => {
    if (annotationToDelete) {
      try {
        const success = await deleteAnnotationFromStorage(annotationToDelete);
        if (success) {
          const newAnnotations = { ...annotations };
          delete newAnnotations[annotationToDelete];
          setAnnotations(newAnnotations);
          setShowAnnotationPanel(false);
          setCurrentAnnotation(null);
          showNotificationMessage('标注已删除');
        } else {
          showNotificationMessage('删除失败，请重试');
        }
      } catch (error: any) {
        console.error('删除标注出错:', error);
        showNotificationMessage('删除失败: ' + error.message);
      }
    }
    setShowDeleteConfirm(false);
    setAnnotationToDelete(null);
  }, [annotationToDelete, annotations, showNotificationMessage]);

  // 新增：批量删除标注（使用新的 blocks API）
  const batchDeleteAnnotations = useCallback(async (annotationIds: string[]) => {
    if (!annotationIds || annotationIds.length === 0) {
      showNotificationMessage('没有选择要删除的标注');
      return false;
    }

    logger.log('开始批量删除标注:', annotationIds);

    try {
      const result = await deleteAnnotationsBatch(annotationIds) as any;

      if (result.success) {
        // 从本地状态中删除这些标注
        const newAnnotations = { ...annotations };
        annotationIds.forEach(id => {
          delete newAnnotations[id];
        });
        setAnnotations(newAnnotations);

        showNotificationMessage(`成功标记 ${annotationIds.length} 个标注为删除状态（可通过管理功能恢复）`);
        return true;
      } else {
        throw new Error(result.message || '批量删除失败');
      }
    } catch (error: any) {
      console.error('批量删除标注失败:', error);
      showNotificationMessage('批量删除失败: ' + error.message);
      return false;
    }
  }, [annotations, showNotificationMessage, setAnnotations]);

  const closeImagePanel = useCallback(() => {
    cleanupAudio();
    setIsGeneratingSpeech(false);
    setShowImagePanel(false);
    setCapturedImage(null);
    setImageQuestion('');
    setImageAnswer('');
    setIsSelecting(false);
    setSelectionRect(null);
  }, [cleanupAudio]);

  const closeAnnotationPanel = useCallback(() => {
    cleanupAudio();
    setIsGeneratingSpeech(false);
    setShowAnnotationPanel(false);
    setCurrentAnnotation(null);
  }, [cleanupAudio]);

  // 复制标注内容到剪贴板
  const copyAnnotationToClipboard = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showNotificationMessage('✅ 已复制到剪贴板');
    } catch (error: any) {
      console.error('复制失败:', error);
      showNotificationMessage('❌ 复制失败');
    }
  }, [showNotificationMessage]);

  // 新增：开始苏格拉底对话
  const startSocraticDialogue = useCallback((context: string) => {
    setSocraticContext(context);
    setSocraticHistory([]);
    setShowSocraticDialog(true);
  }, []);

  // 新增：处理苏格拉底对话
  const handleSocraticQuestion = useCallback(async (userAnswer: string) => {
    try {
      const messages = [
        ...socraticHistory,
        { role: 'user', content: userAnswer }
      ];

      const result = await apiPost('/api/guide/socratic-dialogue', {
        context: socraticContext,
        conversationHistory: messages,
        pdf_fingerprint: pdfFingerprint || null,
      }) as any;

      if (result.success) {
        const newHistory = [
          ...messages,
          { role: 'assistant', content: result.response }
        ];
        setSocraticHistory(newHistory);
        return result.response;
      } else {
        throw new Error(result.error || '对话失败');
      }
    } catch (error: any) {
      console.error('苏格拉底对话失败:', error);
      showNotificationMessage('对话失败: ' + error.message);
      return null;
    }
  }, [socraticContext, socraticHistory, showNotificationMessage]);

  // 新增：关闭苏格拉底对话
  const closeSocraticDialog = useCallback(() => {
    setShowSocraticDialog(false);
    setSocraticContext('');
    setSocraticHistory([]);
  }, []);

  // 新增：创建便利贴（使用新的 blocks API）
  const createStickyNote = useCallback(async ({ noteType, content, selectedText, page, position }: StickyNoteParams) => {
    if (!pdfFingerprint) {
      showNotificationMessage('PDF未加载完成');
      return false;
    }

    const noteId = `sticky_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    const targetPage = page || currentPageRef.current;

    const stickyNote = {
      id: noteId,
      pdfFingerprint: pdfFingerprint,
      page: targetPage,
      noteType: noteType, // 'question', 'idea', 'important'
      content: content,
      selectedText: selectedText || '',
      position: position || { x: 100, y: 100 },
      timestamp: new Date().toISOString(),
      type: 'sticky_note',
      tag: noteType
    };

    try {
      // 保存到数据库（使用新的 blocks API）
      const result = await (saveAnnotationsToStorage as any)(stickyNote);

      if (result.success) {
        // 更新本地状态
        setAnnotations(prev => ({
          ...prev,
          [result.id || noteId]: {
            ...stickyNote,
            id: result.id || noteId
          }
        }));
        showNotificationMessage('便利贴已保存');
        return true;
      } else {
        showNotificationMessage('保存失败');
        return false;
      }
    } catch (error: any) {
      console.error('创建便利贴失败:', error);
      showNotificationMessage('创建便利贴失败: ' + error.message);
      return false;
    }
  }, [pdfFingerprint, showNotificationMessage]);

  const cleanup = useCallback(() => {
    cleanupAudio();
    for (const url of audioCache.values()) {
      URL.revokeObjectURL(url);
    }
    audioCache.clear();
  }, [cleanupAudio, audioCache]);

  const currentPageAnnotations = Object.values(annotations).filter(
    annotation => annotation.page === currentPage
  ).length;

  // 刷新标注数据
  const refreshAnnotations = useCallback(async () => {
    if (!pdfFingerprint) {
      console.warn('无法刷新标注：PDF指纹未生成');
      return;
    }

    try {
      // 根据会话模式刷新标注
      const sessionIdForLoading = sessionMode && currentSession ? currentSession.id : null;
      const { annotations: freshAnnotations, count } = await loadAnnotationsByFingerprint(pdfFingerprint, sessionIdForLoading) as any;
      const modeDesc = sessionMode && currentSession ? `会话「${currentSession.name || currentSession.session_name || '未命名'}」` : '独立模式';
      logger.log(`刷新标注数据(${modeDesc}):`, count, '个');
      setAnnotations(freshAnnotations);
      return { success: true, count };
    } catch (error: any) {
      console.error('刷新标注失败:', error);
      return { success: false, error: error.message };
    }
  }, [pdfFingerprint, sessionMode, currentSession]);

  const totalAnnotations = Object.keys(annotations).length;

  return {
    numPages, scale, annotations, pdfFingerprint, pdfInfo, isGeneratingFingerprint,
    showNotification, notificationMessage, showImagePanel, isSelecting, selectionRect,
    capturedImage, imageQuestion, imageAnswer, imageQuestioning, showAnnotationsList,
    showAnnotationPanel, currentAnnotation, tagFilter, showDeleteConfirm, annotationToDelete,
    showSummary, summary, generatingSummary, isGeneratingSpeech, currentAudio,
    pageRef, currentPageAnnotations, totalAnnotations,
    qaSessions,
    // 苏格拉底对话
    showSocraticDialog, socraticHistory,
    setNumPages, setScale, setAnnotations, setPdfInfo, showNotificationMessage, initializePDF,
    setShowImagePanel, setIsSelecting, setSelectionRect, setCapturedImage, setImageQuestion,
    setImageAnswer, handleImageQuestion, closeImagePanel, setShowAnnotationsList,
    setShowAnnotationPanel, setCurrentAnnotation, setTagFilter, setShowDeleteConfirm,
    setAnnotationToDelete, confirmDeleteAnnotation, closeAnnotationPanel, setShowSummary,
    setSummary, setGeneratingSummary, generateSpeechStream, stopAudio, viewAnnotationCallback,
    copyAnnotationToClipboard,
    cleanup,
    createQASession, updateQASession, removeQASession, handleSessionQuestion, saveSessionAsAnnotation,
    handleQuickScanAnalysis, handleDeepDiveAnalysis, convertToDeepDive, generateCriticalSummary,
    batchDeleteAnnotations,
    refreshAnnotations,  // 新增：刷新标注函数
    // 苏格拉底对话方法
    startSocraticDialogue, handleSocraticQuestion, closeSocraticDialog,
    // 便利贴方法
    createStickyNote
  };
}
